/* One page-owned external player for Shows, Mixtapes and Spotify playlists. Live remains owned
   by asif-radio-player.js. Loading is explicit; playback uses native controls. */
(() => {
  'use strict';
  if (window.__asifOnDemand) return;
  window.__asifOnDemand = true;
  const roots = new Map();
  let active = null;
  let generation = 0;
  let sdkPromise = null;
  const events = ['READY', 'PLAY', 'PLAY_PROGRESS', 'PAUSE', 'FINISH', 'ERROR'];
  const validURL = (value) => {
    try {
      const url = new URL(value);
      return url.protocol === 'https:' && ['soundcloud.com', 'www.soundcloud.com'].includes(url.hostname)
        && !url.username && !url.password && !url.port ? url.href : '';
    } catch { return ''; }
  };
  // Only individual public recordings have an unambiguous position. A set's
  // currentPosition belongs to its current track, not to the complete playlist.
  const progressKey = (value) => {
    const valid = validURL(value);
    if (!valid) return '';
    const url = new URL(valid);
    const parts = url.pathname.split('/').filter(Boolean);
    if (parts.length !== 2 || ['sets', 'tracks', 'likes', 'reposts'].includes(parts[1])) return '';
    return `https://soundcloud.com/${parts.join('/')}`;
  };
  const PROGRESS_KEY = 'asif.radio.recording-progress.v1';
  const PROGRESS_LIMIT = 40;
  const PROGRESS_TTL = 90 * 86400000;
  const readProgress = () => {
    try {
      const raw = JSON.parse(window.localStorage.getItem(PROGRESS_KEY) || '{}');
      if (raw?.version !== 1 || !Array.isArray(raw.entries)) return [];
      const seen = new Set();
      return raw.entries.filter(entry => entry && progressKey(entry.url) === entry.url
        && Number.isFinite(entry.position) && entry.position >= 5000 && entry.position <= 86400000
        && Number.isFinite(entry.updated) && entry.updated <= Date.now()
        && Date.now() - entry.updated <= PROGRESS_TTL)
        .sort((a, b) => b.updated - a.updated)
        .filter(entry => { if (seen.has(entry.url)) return false; seen.add(entry.url); return true; })
        .slice(0, PROGRESS_LIMIT)
        .map(({ url, position, updated }) => ({ url, position, updated }));
    } catch { return []; }
  };
  let progress = readProgress();
  const reflectProgress = (card, position = 0) => {
    card.dataset.resumePosition = String(position);
    const label = card.querySelector('[data-recording-resume]');
    if (label) {
      label.hidden = position === 0;
      const seconds = Math.floor(position / 1000);
      const time = seconds >= 3600
        ? `${Math.floor(seconds / 3600)}:${String(Math.floor(seconds / 60) % 60).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`
        : `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
      label.textContent = (label.dataset.template || '__POSITION__').replace('__POSITION__', time);
    }
    window.dispatchEvent(new CustomEvent('asif:recording-progress', {
      detail: { card, provider: 'soundcloud', positionMs: position }
    }));
  };
  const saveProgress = (record, finished = false) => {
    if (!record?.progressKey || (!finished && !record.progressDirty)) return;
    progress = readProgress().filter(entry => entry.url !== record.progressKey);
    if (!finished && record.position >= 5000) {
      progress.unshift({ url: record.progressKey, position: record.position, updated: Date.now() });
    }
    progress = progress.slice(0, PROGRESS_LIMIT);
    try { window.localStorage.setItem(PROGRESS_KEY, JSON.stringify({ version: 1, entries: progress })); }
    catch { /* Storage restrictions must never stop playback. */ }
    record.progressDirty = false;
    record.lastProgressWrite = Date.now();
    reflectProgress(record.card, finished ? 0 : record.position >= 5000 ? record.position : 0);
  };
  const restoreProgress = (record) => {
    if (!current(record) || !record.ready || !record.wantPlay || !record.resumePosition) return;
    const position = record.resumePosition;
    record.resumePosition = 0; // Only once per widget, never rewind an in-memory pause.
    try { record.widget.seekTo(position); } catch { /* Native player remains usable. */ }
  };
  const sdk = () => {
    if (window.SC?.Widget) return Promise.resolve(window.SC);
    if (sdkPromise) return sdkPromise;
    sdkPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://w.soundcloud.com/player/api.js';
      script.async = true;
      let timer;
      let settled = false;
      const finish = (error) => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timer);
        script.onload = script.onerror = null;
        if (error) { script.remove(); reject(error); }
        else resolve(window.SC);
      };
      script.onload = () => finish(window.SC?.Widget ? null : new Error('SDK unavailable'));
      script.onerror = () => finish(new Error('SDK unavailable'));
      timer = window.setTimeout(() => finish(new Error('SDK timeout')), 15000);
      document.head.append(script);
    }).catch((error) => { sdkPromise = null; throw error; });
    return sdkPromise;
  };
  let spotifyAPI = null;
  let spotifyPromise = null;
  const spotifySDK = () => {
    if (spotifyAPI) return Promise.resolve(spotifyAPI);
    if (spotifyPromise) return spotifyPromise;
    spotifyPromise = new Promise((resolve, reject) => {
      const previous = window.onSpotifyIframeApiReady;
      const script = document.createElement('script');
      script.src = 'https://open.spotify.com/embed/iframe-api/v1';
      script.async = true;
      let timer;
      let settled = false;
      const finish = (api, error) => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timer);
        script.onerror = null;
        if (window.onSpotifyIframeApiReady === callback) window.onSpotifyIframeApiReady = previous;
        if (error) { script.remove(); reject(error); }
        else { spotifyAPI = api; resolve(api); }
      };
      const callback = (api) => {
        try { previous?.(api); } catch {}
        finish(api, typeof api?.createController === 'function' ? null : new Error('Spotify API unavailable'));
      };
      window.onSpotifyIframeApiReady = callback;
      script.onerror = () => finish(null, new Error('Spotify API unavailable'));
      timer = window.setTimeout(() => finish(null, new Error('Spotify API timeout')), 15000);
      document.head.append(script);
    }).catch((error) => { spotifyPromise = null; throw error; });
    return spotifyPromise;
  };
  const spotifyURL = (value) => {
    try {
      const url = new URL(value);
      if (url.protocol !== 'https:' || url.hostname !== 'open.spotify.com' || url.username || url.password || url.port) return '';
      const match = url.pathname.match(/^\/(?:intl-[a-z]{2}\/)?playlist\/([a-zA-Z0-9]{22})\/?$/);
      return match ? `spotify:playlist:${match[1]}` : '';
    } catch { return ''; }
  };
  const current = (record) => active === record && record.generation === generation && record.card.isConnected;
  const reflect = (record, state) => {
    const expanded = ['loading', 'ready', 'starting', 'playing', 'blocked'].includes(state);
    if (!expanded && record.host.contains(document.activeElement)) record.button.focus({ preventScroll: true });
    record.host.hidden = !expanded;
    record.button.setAttribute('aria-expanded', String(expanded));
    record.opener?.setAttribute('aria-expanded', String(expanded));
    record.opener?.setAttribute('aria-label', `${record.card.dataset.recordingTitle || 'Aufnahme'}: Player ${expanded ? 'schließen' : 'öffnen'}`);
    record.playback = state;
    record.button.dataset.playback = state;
    record.card.dataset.playback = state;
    const action = state === 'playing' ? 'pausieren' : (state === 'starting' || (state === 'loading' && record.wantPlay)) ? 'Start abbrechen' : 'abspielen';
    record.button.setAttribute('aria-label', `${record.card.dataset.recordingTitle || 'Aufnahme'} ${action}`);
  };
  // A pause acknowledgement is not permission to play again. Only a new row
  // request or the deliberately visible native player may claim audio ownership.
  const acceptStart = (record) => {
    if (!current(record)) return false;
    if (record.pausePending || (!record.wantPlay && record.host.hidden)) {
      try { (record.controller || record.widget).pause(); } catch { teardown(); }
      return false;
    }
    record.wantPlay = true;
    return true;
  };
  const settlePaused = (record) => {
    record.playAttempt = (record.playAttempt || 0) + 1;
    record.wantPlay = false;
    record.spotifyPlaying = false;
    window.clearTimeout(record.startTimer);
    reflect(record, 'paused');
  };
  const requestPlay = (record) => {
    if (!current(record)) return;
    const attempt = record.playAttempt = (record.playAttempt || 0) + 1;
    record.wantPlay = true;
    record.pausePending = false;
    window.clearTimeout(record.startTimer);
    reflect(record, 'starting');
    window.dispatchEvent(new CustomEvent('asif:audio-owner', { detail: { owner: record.provider } }));
    record.status.textContent = 'Wiedergabe startet. Falls sie nicht beginnt, nutze Play im eingeblendeten Player.';
    const blocked = () => {
      if (!current(record) || attempt !== record.playAttempt || record.playback === 'playing') return;
      reflect(record, 'blocked');
      record.status.textContent = 'Nutze Play im eingeblendeten Player.';
    };
    record.startTimer = window.setTimeout(blocked, 8000);
    restoreProgress(record);
    try { Promise.resolve((record.controller || record.widget).play()).catch(blocked); }
    catch { blocked(); }
  };
  const teardown = (message = '') => {
    generation += 1;
    const record = active;
    saveProgress(record);
    active = null;
    if (!record) return;
    window.clearTimeout(record.timer);
    window.clearTimeout(record.startTimer);
    if (record.widget) events.forEach((name) => {
      try { record.widget.unbind(window.SC.Widget.Events[name]); } catch {}
    });
    // Removing the frame also stops audio; asynchronous pause messages cannot
    // allow a late PLAY event to reclaim ownership after a newer live request.
    try { record.controller?.destroy(); } catch {}
    record.host.replaceChildren();
    record.frame?.remove();
    record.host.hidden = true;
    record.button.hidden = false;
    record.button.disabled = false;
    reflect(record, 'idle');
    record.status.textContent = message;
    record.card.removeAttribute('aria-busy');
  };
  const pauseRecord = (record) => {
    saveProgress(record);
    record.playAttempt = (record.playAttempt || 0) + 1;
    record.wantPlay = false;
    record.pausePending = true;
    record.spotifyPlaying = false;
    window.clearTimeout(record.startTimer);
    reflect(record, 'paused');
    record.status.textContent = 'Wiedergabe pausiert.';
    try { (record.controller || record.widget)?.pause(); } catch { teardown(); }
  };
  const ready = (record) => {
    if (record.wantPlay) requestPlay(record);
    else { reflect(record, 'ready'); record.status.textContent = ''; }
  };
  const load = async (card, autoplay = true) => {
    const button = card.querySelector('[data-recording-load]');
    const host = card.querySelector('[data-recording-player]');
    const status = card.querySelector('[data-recording-status]');
    if (!button || !host || !status) return;
    const provider = card.dataset.provider === 'spotify' ? 'spotify' : 'soundcloud';
    const providerName = provider === 'spotify' ? 'Spotify' : 'SoundCloud';
    const url = provider === 'spotify' ? spotifyURL(card.dataset.spotifyUrl) : validURL(card.dataset.soundcloudUrl);
    if (!url) { status.textContent = `Dieser Player ist gerade nicht verfügbar. Nutze den ${providerName}-Link.`; return; }
    if (active?.card === card) {
      const record = active;
      if (!autoplay) {
        if (!host.hidden) {
          if (record.playback === 'loading') teardown();
          else if (record.playback === 'starting') pauseRecord(record);
          else if (['playing', 'blocked'].includes(record.playback)) pauseRecord(record);
          else reflect(record, 'paused');
        } else { record.pausePending = false; reflect(record, 'ready'); status.textContent = ''; }
      } else if (record.playback === 'loading') {
        if (record.wantPlay) teardown('Start abgebrochen.');
        else { record.wantPlay = true; reflect(record, 'loading'); }
      } else if (record.playback === 'starting') pauseRecord(record);
      else if (record.playback === 'playing') pauseRecord(record);
      else requestPlay(record);
      return;
    }
    teardown();
    const key = provider === 'soundcloud' ? progressKey(url) : '';
    progress = readProgress();
    const resumePosition = progress.find(entry => entry.url === key)?.position || 0;
    const record = { card, button, opener: card.querySelector('[data-recording-open]'), host, status, provider, generation, widget: null, frame: null, timer: 0, startTimer: 0, wantPlay: autoplay, pausePending: false,
      progressKey: key, resumePosition, position: resumePosition, progressDirty: false, lastProgressWrite: 0 };
    active = record;
    button.disabled = false;
    reflect(record, 'loading');
    card.setAttribute('aria-busy', 'true');
    status.textContent = `${providerName}-Player wird geladen.`;
    const fail = () => {
      if (!current(record)) return;
      teardown(`Der Player konnte nicht geladen werden. Versuche es erneut oder öffne ${providerName}.`);
      reflect(record, 'error');
    };
    try {
      if (provider === 'spotify') {
        const api = await spotifySDK();
        if (!current(record)) return;
        const slot = document.createElement('div');
        host.hidden = false;
        host.replaceChildren(slot);
        record.timer = window.setTimeout(fail, 20000);
        api.createController(slot, { uri: url, width: '100%', height: 560 }, (controller) => {
          if (!current(record)) { try { controller.destroy(); } catch {} return; }
          record.controller = controller;
          const started = () => {
            if (!acceptStart(record)) return;
            window.clearTimeout(record.startTimer);
            record.spotifyPlaying = true;
            reflect(record, 'playing');
            window.dispatchEvent(new CustomEvent('asif:audio-owner', { detail: { owner: 'spotify' } }));
            status.textContent = 'Du hörst die Playlist auf Spotify. Das Live-Radio ist pausiert.';
          };
          controller.addListener('ready', () => {
            if (!current(record) || record.ready) return;
            record.ready = true;
            window.clearTimeout(record.timer);
            card.removeAttribute('aria-busy');
            const frame = host.querySelector('iframe');
            if (frame) {
              frame.title = `${card.dataset.recordingTitle || 'Playlist'} auf Spotify`;

            }
            ready(record);
          });
          controller.addListener('playback_started', started);
          controller.addListener('playback_update', (event) => {
            if (!current(record)) return;
            if (event.data?.isPaused === false) {
              if (!acceptStart(record)) return;
              if (!record.spotifyPlaying || record.playback !== 'playing') started();
              record.spotifyPlaying = true;
              const message = event.data.isBuffering ? 'Spotify lädt die Wiedergabe.' : 'Du hörst die Playlist auf Spotify. Das Live-Radio ist pausiert.';
              if (status.textContent !== message) status.textContent = message;
            } else if (event.data?.isPaused === true) {
              record.pausePending = false;
              if (['loading', 'ready', 'starting', 'blocked'].includes(record.playback)) return;
              settlePaused(record);
              const message = 'Playlist pausiert. Du kannst im Spotify-Player weiterhören.';
              if (status.textContent !== message) status.textContent = message;
            }
          });
        });
        return;
      }
      const SC = await sdk();
      if (!current(record)) return;
      const frame = document.createElement('iframe');
      record.frame = frame;
      frame.title = `${card.dataset.recordingTitle || 'Aufnahme'} auf SoundCloud`;
      frame.width = '100%';
      frame.height = '166';
      frame.setAttribute('scrolling', 'no');
      frame.setAttribute('frameborder', '0');
      frame.setAttribute('allow', 'autoplay');
      const playerURL = new URL('https://w.soundcloud.com/player/');
      playerURL.search = new URLSearchParams({ url, auto_play: 'false', color: '#e5df70', hide_related: 'true', show_comments: 'false', show_reposts: 'false', show_teaser: 'false', visual: 'false', single_active: 'true' }).toString();
      frame.src = playerURL.href;
      host.hidden = false;
      host.replaceChildren(frame);
      const widget = SC.Widget(frame);
      record.widget = widget;
      record.timer = window.setTimeout(fail, 20000);
      widget.bind(SC.Widget.Events.READY, () => {
        if (!current(record) || record.ready) return;
        record.ready = true;
        window.clearTimeout(record.timer);
        card.removeAttribute('aria-busy');
        ready(record);
      });
      widget.bind(SC.Widget.Events.PLAY, () => {
        if (!current(record)) return;
        if (!acceptStart(record)) return;
        restoreProgress(record);
        window.clearTimeout(record.startTimer);
        reflect(record, 'playing');
        window.dispatchEvent(new CustomEvent('asif:audio-owner', { detail: { owner: 'soundcloud' } }));
        status.textContent = 'Du hörst die Aufnahme auf SoundCloud. Das Live-Radio ist pausiert.';
      });
      widget.bind(SC.Widget.Events.PAUSE, () => {
        if (!current(record)) return;
        saveProgress(record);
        record.pausePending = false;
        if (!['loading', 'ready', 'starting', 'blocked'].includes(record.playback)) { settlePaused(record); status.textContent = 'Aufnahme pausiert. Du kannst im Player weiterhören.'; }
      });
      widget.bind(SC.Widget.Events.FINISH, () => {
        if (current(record)) {
          record.position = 0; record.resumePosition = 0;
          saveProgress(record, true);
          settlePaused(record); status.textContent = 'Aufnahme beendet. Wähle eine weitere Aufnahme oder starte das Live-Radio.';
        }
      });
      widget.bind(SC.Widget.Events.PLAY_PROGRESS, (event) => {
        if (!current(record) || !record.progressKey || !record.wantPlay || record.playback !== 'playing') return;
        const position = event?.currentPosition;
        if (!Number.isFinite(position) || position < 0 || position > 86400000) return;
        record.position = Math.floor(position);
        record.progressDirty = true;
        if (Date.now() - record.lastProgressWrite >= 10000) saveProgress(record);
      });
      widget.bind(SC.Widget.Events.ERROR, fail);
    } catch { fail(); }
  };
  const mount = (root) => {
    if (roots.has(root)) return;
    root.classList.add('radio-on-demand-ready');
    root.querySelectorAll('[data-recording-load]').forEach((button) => { button.hidden = false; });
    progress = readProgress();
    root.querySelectorAll('[data-radio-recording]').forEach(card => {
      const key = card.dataset.provider === 'spotify' ? '' : progressKey(card.dataset.soundcloudUrl);
      if (key) reflectProgress(card, progress.find(entry => entry.url === key)?.position || 0);
    });
    const click = (event) => {
      const button = event.target.closest('[data-recording-load], [data-recording-open]');
      if (!button || !root.contains(button)) return;
      const card = button.closest('[data-radio-recording]');
      if (card) load(card, !button.hasAttribute('data-recording-open'));
    };
    root.addEventListener('click', click);
    roots.set(root, () => {
      if (active && root.contains(active.card)) teardown();
      root.removeEventListener('click', click);
      roots.delete(root);
    });
  };
  const scan = () => document.querySelectorAll('[data-radio-on-demand]').forEach(mount);
  window.addEventListener('asif:audio-owner', (event) => {
    if (event.detail?.owner === 'live') teardown('Live-Radio ausgewählt. Du kannst die Aufnahme jederzeit erneut laden.');
  });
  window.addEventListener('asif:audio-stop', () => {
    if (!active) return;
    // A not-yet-ready widget has no confirmed paused state. Retire the load so
    // a late READY cannot revive a sleep-timer-cancelled playback request.
    if (!active.ready) teardown();
    else pauseRecord(active);
  });
  document.addEventListener('asif:page:unload', () => roots.forEach((cleanup) => cleanup()));
  document.addEventListener('asif:page', scan);
  document.addEventListener('shopify:section:load', scan);
  document.addEventListener('shopify:section:unload', (event) => roots.forEach((cleanup, root) => {
    if (event.target === root || event.target.contains(root)) cleanup();
  }));
  // Do not stop on visibilitychange: background listening is intentional.
  document.addEventListener('visibilitychange', () => { if (document.hidden) saveProgress(active); });
  window.addEventListener('pagehide', () => saveProgress(active));
  window.addEventListener('pageshow', scan);
  scan();
})();
